import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import {
  GoogleAuthProvider,
  getRedirectResult,
  signInWithPopup,
  signInWithRedirect,
  type User,
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { ensureAccountDirectory, respondToInvite, subscribeAccessibleLists } from '../../data'
import { requireAuth, requireDb, requireFunctions } from '../../firebase'
import type { CloudShoppingList, Language, ListInvite } from '../../models'

export interface ListMember {
  userId: string
  email: string | null
  role: string
}

export interface ListAccess {
  listId: string
  ownerUserId: string
  ownerEmail: string | null
  isOwner: boolean
  currentUserRole: string
  members: ListMember[]
  pendingInvites: ListInvite[]
}

export interface PremiumEntitlementState {
  isPremium: boolean
  checking: boolean
  error: string | null
  validUntil: number | null
}

export interface BlockedUser {
  userId: string
  email: string | null
  createdAt: number
}

export interface CommunitySuggestion {
  communityItemId: string
  title: string
  categoryId: string
  categoryName: string
  votes: number
  matchScore: number
}

export interface ReportTarget {
  targetType: string
  targetId: string
  targetOwnerUid?: string | null
  source: string
  listId?: string | null
  label: string
}

const now = () => Date.now()
const normalizeEmail = (value: string) => value.trim().toLowerCase()
const isLikelyEmail = (value: string) => {
  const at = value.indexOf('@')
  return at > 0 && at === value.lastIndexOf('@') && at < value.length - 1 && value.slice(at + 1).includes('.')
}
const displayEmail = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const normalized = normalizeEmail(value)
  return isLikelyEmail(normalized) ? normalized : null
}
const hashEmail = async (value: string) => {
  const bytes = new TextEncoder().encode(value)
  const hash = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(hash)).map((part) => part.toString(16).padStart(2, '0')).join('')
}
const nextUpdatedAt = (existing: unknown) => Math.max(now(), (typeof existing === 'number' ? existing : Number.MIN_SAFE_INTEGER) + 1)

function shouldUseGoogleRedirect() {
  const nav = navigator as Navigator & { standalone?: boolean }
  const standalone = nav.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches === true
  const appleMobile = /iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  return standalone || appleMobile
}

/**
 * Firebase popup auth is unreliable in installed iOS PWAs. Match native account semantics by
 * using redirect auth on Apple mobile/standalone surfaces and retaining popup auth elsewhere.
 */
export async function googleSignInForPwa(): Promise<User | null> {
  const auth = requireAuth()
  const provider = new GoogleAuthProvider()
  if (shouldUseGoogleRedirect()) {
    await signInWithRedirect(auth, provider)
    return null
  }
  try {
    const result = await signInWithPopup(auth, provider)
    await ensureAccountDirectory(result.user)
    return result.user
  } catch (error) {
    const code = (error as { code?: string })?.code ?? ''
    if (code === 'auth/popup-blocked' || code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      await signInWithRedirect(auth, provider)
      return null
    }
    throw error
  }
}

export async function completeGoogleRedirect() {
  const result = await getRedirectResult(requireAuth())
  if (result?.user) await ensureAccountDirectory(result.user)
  return result?.user ?? null
}

export interface AccessibleListBinding {
  refresh: () => void
  unsubscribe: () => void
}

/**
 * A small rebindable wrapper around the normal owned/shared/item listeners. Android deliberately
 * tears down and re-attaches these listeners after accepting an invitation because Firestore can
 * emit the member query from local cache before nested list/item reads are server-authoritative.
 */
export function bindAccessibleLists(
  uid: string,
  callback: (lists: CloudShoppingList[]) => void,
): AccessibleListBinding {
  let active = true
  let unsubscribe: Unsubscribe | null = null
  const refresh = () => {
    if (!active) return
    unsubscribe?.()
    unsubscribe = subscribeAccessibleLists(uid, callback)
  }
  refresh()
  return {
    refresh,
    unsubscribe: () => {
      active = false
      unsubscribe?.()
      unsubscribe = null
    },
  }
}

export async function respondToInviteWithRebind(
  invite: ListInvite,
  accept: boolean,
  binding: AccessibleListBinding | null,
) {
  await respondToInvite(invite, accept)
  if (accept) binding?.refresh()
}

const inviteFromDoc = (snapshot: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>): ListInvite | null => {
  if (!snapshot.exists()) return null
  const data = snapshot.data()
  const listId = data.list_id
  const ownerUserId = data.owner_id
  const recipientUserId = data.recipient_uid
  const recipientEmail = data.recipient_email
  const status = data.status
  if (![listId, ownerUserId, recipientUserId, recipientEmail, status].every((value) => typeof value === 'string')) return null
  return {
    id: snapshot.id,
    listId,
    listName: typeof data.list_name === 'string' ? data.list_name : 'Shopping list',
    ownerUserId,
    ownerEmail: displayEmail(data.owner_email),
    recipientUserId,
    recipientEmail,
    status,
    createdAt: typeof data.created_at === 'number' ? data.created_at : 0,
    updatedAt: typeof data.updated_at === 'number' ? data.updated_at : undefined,
  }
}

export async function loadListAccess(listId: string): Promise<ListAccess> {
  const user = requireAuth().currentUser
  if (!user) throw new Error('No signed-in Firebase user.')
  const db = requireDb()
  const listSnapshot = await getDoc(doc(db, 'lists', listId))
  if (!listSnapshot.exists()) throw new Error('Shopping list not found.')
  const data = listSnapshot.data()
  const ownerUserId = data.owner_id as string | undefined
  if (!ownerUserId) throw new Error('Shopping list owner is missing.')
  const members = (data.members ?? {}) as Record<string, unknown>
  const memberEmails = (data.member_emails ?? {}) as Record<string, unknown>
  const role = members[user.uid]
  const isOwner = ownerUserId === user.uid
  if (!isOwner && role !== 'editor' && role !== 'viewer' && role !== 'owner') {
    throw new Error('You no longer have access to this list.')
  }

  let relatedInvites: ListInvite[]
  if (isOwner) {
    const snapshots = await getDocs(query(collection(db, 'list_invites'), where('owner_id', '==', user.uid)))
    relatedInvites = snapshots.docs.map(inviteFromDoc).filter((value): value is ListInvite => Boolean(value && value.listId === listId))
  } else {
    const invite = inviteFromDoc(await getDoc(doc(db, 'list_invites', `${listId}_${user.uid}`)))
    relatedInvites = invite ? [invite] : []
  }

  const acceptedByUserId = new Map(
    relatedInvites.filter((invite) => invite.status === 'accepted').map((invite) => [invite.recipientUserId, invite]),
  )
  const activeMembers: ListMember[] = Object.entries(members)
    .filter(([memberUid, memberRole]) => memberUid !== ownerUserId && typeof memberRole === 'string')
    .map(([memberUid, memberRole]) => ({
      userId: memberUid,
      email: displayEmail(memberEmails[memberUid])
        ?? displayEmail(acceptedByUserId.get(memberUid)?.recipientEmail)
        ?? (memberUid === user.uid ? displayEmail(user.email) : null),
      role: memberRole as string,
    }))
    .sort((left, right) => (left.email ?? left.userId).localeCompare(right.email ?? right.userId))

  const ownerEmail = displayEmail(data.owner_email)
    ?? (isOwner ? displayEmail(user.email) : null)
    ?? relatedInvites.map((invite) => displayEmail(invite.ownerEmail)).find(Boolean)
    ?? null

  return {
    listId,
    ownerUserId,
    ownerEmail,
    isOwner,
    currentUserRole: isOwner ? 'owner' : typeof role === 'string' ? role : 'editor',
    members: activeMembers,
    pendingInvites: isOwner
      ? relatedInvites.filter((invite) => invite.status === 'pending').sort((a, b) => b.createdAt - a.createdAt)
      : [],
  }
}

/** Keep an open access manager live across invite/member changes, mirroring ListAccessService.observeAccess. */
export function subscribeListAccess(
  listId: string,
  onChanged: (access: ListAccess) => void,
  onError: (error: unknown) => void = () => undefined,
): Unsubscribe {
  const user = requireAuth().currentUser
  if (!user) return () => undefined
  const db = requireDb()
  let active = true
  let timer: number | null = null
  const registrations: Unsubscribe[] = []

  const refresh = () => {
    if (timer != null) window.clearTimeout(timer)
    timer = window.setTimeout(() => {
      void loadListAccess(listId).then((value) => {
        if (active) onChanged(value)
      }).catch((error) => {
        if (active) onError(error)
      })
    }, 30)
  }

  registrations.push(onSnapshot(doc(db, 'lists', listId), refresh, onError))
  registrations.push(onSnapshot(query(collection(db, 'list_invites'), where('owner_id', '==', user.uid)), refresh, onError))
  registrations.push(onSnapshot(query(collection(db, 'list_invites'), where('recipient_uid', '==', user.uid)), refresh, onError))
  refresh()

  return () => {
    active = false
    if (timer != null) window.clearTimeout(timer)
    registrations.forEach((unsubscribe) => unsubscribe())
  }
}

export async function inviteToListManaged(listId: string, listName: string, recipientEmail: string) {
  const user = requireAuth().currentUser
  if (!user) throw new Error('Sign in first.')
  const db = requireDb()
  const email = normalizeEmail(recipientEmail)
  if (!isLikelyEmail(email)) throw new Error('Enter a valid email address.')
  if (email === normalizeEmail(user.email ?? '')) throw new Error('You cannot invite yourself.')

  const directory = await getDoc(doc(db, 'email_directory', await hashEmail(email)))
  const recipientUid = directory.data()?.uid as string | undefined
  if (!recipientUid) throw new Error('No SHELFWAY account was found for that email address.')

  const listRef = doc(db, 'lists', listId)
  const list = await getDoc(listRef)
  if (!list.exists() || list.data().owner_id !== user.uid) throw new Error('Only the list owner can invite people.')
  const members = (list.data().members ?? {}) as Record<string, unknown>
  if (members[recipientUid] != null) throw new Error('That person already has access to this list.')

  const inviteRef = doc(db, 'list_invites', `${listId}_${recipientUid}`)
  const existing = inviteFromDoc(await getDoc(inviteRef))
  if (existing?.status === 'pending') throw new Error('An invitation is already waiting for that person.')
  if (existing) await deleteDoc(inviteRef)

  const timestamp = now()
  await setDoc(inviteRef, {
    list_id: listId,
    list_name: listName,
    owner_id: user.uid,
    owner_email: displayEmail(user.email),
    recipient_uid: recipientUid,
    recipient_email: email,
    status: 'pending',
    created_at: timestamp,
    updated_at: timestamp,
  })

  const identityUpdate: Record<string, unknown> = { [`member_emails.${recipientUid}`]: email }
  const ownerEmail = displayEmail(user.email)
  if (ownerEmail) identityUpdate.owner_email = ownerEmail
  void updateDoc(listRef, identityUpdate).catch((error) => console.warn('Shelfway optional sharing identity update failed', error))
}

export async function cancelListInvite(inviteId: string) {
  const user = requireAuth().currentUser
  if (!user) throw new Error('Sign in first.')
  const db = requireDb()
  const inviteRef = doc(db, 'list_invites', inviteId)
  const invite = inviteFromDoc(await getDoc(inviteRef))
  if (!invite) throw new Error('Invitation not found.')
  if (invite.ownerUserId !== user.uid) throw new Error('Only the list owner can cancel this invitation.')
  if (invite.status !== 'pending') throw new Error('This invitation is no longer pending.')
  await deleteDoc(inviteRef)

  void (async () => {
    const listRef = doc(db, 'lists', invite.listId)
    const list = await getDoc(listRef)
    const members = (list.data()?.members ?? {}) as Record<string, unknown>
    if (members[invite.recipientUserId] == null) {
      await updateDoc(listRef, { [`member_emails.${invite.recipientUserId}`]: deleteField() })
    }
  })().catch((error) => console.warn('Shelfway optional invite identity cleanup failed', error))
}

export async function removeListMember(listId: string, memberUserId: string) {
  const user = requireAuth().currentUser
  if (!user) throw new Error('Sign in first.')
  if (memberUserId === user.uid) throw new Error('The owner cannot remove themselves from their own list.')
  const db = requireDb()
  const listRef = doc(db, 'lists', listId)
  const inviteRef = doc(db, 'list_invites', `${listId}_${memberUserId}`)

  await runTransaction(db, async (transaction) => {
    const list = await transaction.get(listRef)
    const invite = await transaction.get(inviteRef)
    if (!list.exists() || list.data().owner_id !== user.uid) throw new Error('Only the list owner can remove people.')
    transaction.update(listRef, {
      [`members.${memberUserId}`]: deleteField(),
      member_uids: arrayRemove(memberUserId),
      updated_at: nextUpdatedAt(list.data().updated_at),
    })
    if (invite.exists()) transaction.delete(inviteRef)
  })

  void updateDoc(listRef, { [`member_emails.${memberUserId}`]: deleteField() })
    .catch((error) => console.warn('Shelfway optional member identity cleanup failed', error))
}

export async function leaveListManaged(listId: string) {
  const user = requireAuth().currentUser
  if (!user) throw new Error('Sign in first.')
  const db = requireDb()
  const listRef = doc(db, 'lists', listId)
  const inviteRef = doc(db, 'list_invites', `${listId}_${user.uid}`)

  await runTransaction(db, async (transaction) => {
    const list = await transaction.get(listRef)
    const invite = await transaction.get(inviteRef)
    if (!list.exists()) throw new Error('Shopping list not found.')
    if (list.data().owner_id === user.uid) throw new Error('The owner cannot leave their own list.')
    const members = (list.data().members ?? {}) as Record<string, unknown>
    if (members[user.uid] !== 'editor' && members[user.uid] !== 'viewer') throw new Error('You no longer have access to this list.')
    transaction.update(listRef, {
      [`members.${user.uid}`]: deleteField(),
      member_uids: arrayRemove(user.uid),
      updated_at: nextUpdatedAt(list.data().updated_at),
    })
    if (invite.exists()) transaction.delete(inviteRef)
  })
}

export function subscribePremiumEntitlement(
  uid: string,
  callback: (state: PremiumEntitlementState) => void,
): Unsubscribe {
  callback({ isPremium: false, checking: true, error: null, validUntil: null })
  return onSnapshot(doc(requireDb(), 'premium_entitlements', uid), (snapshot) => {
    const value = snapshot.data()?.valid_until as { toMillis?: () => number; toDate?: () => Date } | undefined
    const validUntil = value?.toMillis?.() ?? value?.toDate?.().getTime() ?? null
    const isPremium = snapshot.data()?.status === 'active' && validUntil != null && validUntil > now()
    callback({ isPremium, checking: false, error: null, validUntil })
  }, (error) => {
    // Fail closed exactly like Android: unknown entitlement state must never unlock Premium content.
    callback({ isPremium: false, checking: true, error: error.message, validUntil: null })
  })
}

export async function submitContentReport(target: ReportTarget, reason: string, details: string) {
  await httpsCallable(requireFunctions(), 'submitContentReport')({
    targetType: target.targetType,
    targetId: target.targetId,
    targetOwnerUid: target.targetOwnerUid ?? '',
    reason,
    details: details.trim().slice(0, 500),
    source: target.source,
    listId: target.listId ?? '',
  })
}

export async function blockUser(blockedUid: string, blockedEmail: string | null, listId: string) {
  await httpsCallable(requireFunctions(), 'setUserBlock')({
    blockedUid,
    blockedEmail: blockedEmail ?? '',
    listId,
    blocked: true,
  })
}

export async function unblockUser(blockedUid: string) {
  await httpsCallable(requireFunctions(), 'setUserBlock')({ blockedUid, blocked: false })
}

export async function listBlockedUsers(): Promise<BlockedUser[]> {
  const result = await httpsCallable(requireFunctions(), 'listUserBlocks')()
  const data = result.data as { blocks?: unknown[] } | null
  return (data?.blocks ?? []).map((raw) => {
    const row = raw as Record<string, unknown>
    return {
      userId: typeof row.userId === 'string' ? row.userId : '',
      email: typeof row.email === 'string' && row.email.trim() ? row.email : null,
      createdAt: typeof row.createdAt === 'number' ? row.createdAt : 0,
    }
  }).filter((row) => row.userId)
}

const normalizeSearch = (value: string) => value
  .normalize('NFKC')
  .toLocaleLowerCase()
  .replace(/[^\p{L}\p{N}]+/gu, ' ')
  .trim()
  .replace(/\s+/g, ' ')

const bigrams = (value: string) => {
  const compact = value.replaceAll(' ', '')
  const result = new Set<string>()
  for (let index = 0; index < compact.length - 1; index += 1) result.add(compact.slice(index, index + 2))
  return result
}

export function fuzzyScore(leftRaw: string, rightRaw: string) {
  const left = normalizeSearch(leftRaw)
  const right = normalizeSearch(rightRaw)
  if (!left || !right) return 0
  if (left === right) return 1
  if (right.startsWith(left) || left.startsWith(right)) return 0.94
  if (right.includes(left) || left.includes(right)) return 0.88

  const leftTokens = new Set(left.split(' ').filter(Boolean))
  const rightTokens = new Set(right.split(' ').filter(Boolean))
  const union = new Set([...leftTokens, ...rightTokens])
  const tokenIntersection = [...leftTokens].filter((token) => rightTokens.has(token)).length
  const tokenScore = union.size ? tokenIntersection / union.size : 0

  const leftPairs = bigrams(left)
  const rightPairs = bigrams(right)
  const pairIntersection = [...leftPairs].filter((pair) => rightPairs.has(pair)).length
  const dice = leftPairs.size && rightPairs.size ? (2 * pairIntersection) / (leftPairs.size + rightPairs.size) : 0
  return Math.max(tokenScore, dice * 0.9, tokenScore * 0.65 + dice * 0.35)
}

export async function searchCommunityItems(
  language: Language,
  rawQuery: string,
  categories: Array<{ id: string; name: string }>,
  maxResults = 6,
): Promise<CommunitySuggestion[]> {
  const normalizedQuery = normalizeSearch(rawQuery)
  if (normalizedQuery.length < 2 || categories.length === 0) return []
  const searchToken = normalizedQuery.slice(0, 2)
  const snapshots = await getDocs(query(
    collection(requireDb(), 'community_items'),
    where('language', '==', language.toLowerCase()),
    where('search_terms', 'array-contains', searchToken),
    limit(80),
  ))

  const suggestions = snapshots.docs.flatMap((snapshot): CommunitySuggestion[] => {
    const data = snapshot.data()
    const moderationStatus = typeof data.moderation_status === 'string' ? data.moderation_status.toLowerCase() : ''
    if (moderationStatus === 'flagged' || moderationStatus === 'hidden') return []
    if (typeof data.normalized_item !== 'string') return []
    const itemScore = fuzzyScore(normalizedQuery, data.normalized_item)
    if (itemScore < 0.42) return []

    const options = Array.isArray(data.category_options) ? data.category_options : []
    const rankedOptions = options.map((raw: unknown) => {
      const option = raw as Record<string, unknown>
      return {
        normalizedLabel: typeof option.normalized_label === 'string' ? option.normalized_label : '',
        label: typeof option.label === 'string' ? option.label : '',
        count: typeof option.count === 'number' ? option.count : 0,
      }
    }).filter((option) => option.normalizedLabel && option.count > 0).sort((a, b) => b.count - a.count)

    for (const option of rankedOptions) {
      let best: { category: { id: string; name: string }; score: number } | null = null
      for (const category of categories) {
        const score = fuzzyScore(option.normalizedLabel, normalizeSearch(category.name))
        if (!best || score > best.score) best = { category, score }
      }
      if (best && best.score >= 0.48) {
        return [{
          communityItemId: snapshot.id,
          title: typeof data.display_item === 'string' && data.display_item.trim() ? data.display_item : data.normalized_item,
          categoryId: best.category.id,
          categoryName: best.category.name,
          votes: option.count,
          matchScore: itemScore * 0.72 + best.score * 0.28,
        }]
      }
    }
    return []
  })

  const seen = new Set<string>()
  return suggestions.sort((a, b) => b.matchScore - a.matchScore || b.votes - a.votes).filter((suggestion) => {
    const key = normalizeSearch(suggestion.title)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  }).slice(0, maxResults)
}

export async function recordCommunityItemEvidence(language: Language, itemTitle: string, categoryLabel: string) {
  const item = itemTitle.trim().slice(0, 120)
  const label = categoryLabel.trim().slice(0, 120)
  if (normalizeSearch(item).length < 2 || !normalizeSearch(label)) return
  await httpsCallable(requireFunctions(), 'recordCommunityItemEvidence')({
    language: language.toLowerCase(),
    item,
    categoryLabel: label,
  })
}

/** Helper used by access UI to expose Android-equivalent role labels without duplicating contract logic. */
export function roleLabel(language: Language, role: string) {
  if (role === 'owner') return language === 'sv' ? 'Ägare' : 'Owner'
  if (role === 'viewer') return language === 'sv' ? 'Läsare' : 'Viewer'
  return language === 'sv' ? 'Redigerare' : 'Editor'
}

/** Keep imports of arrayUnion alive as a contract-level compile check for accepted self-join semantics. */
export const acceptedMembershipMutation = (uid: string) => ({
  [`members.${uid}`]: 'editor',
  member_uids: arrayUnion(uid),
  updated_at: now(),
})
